/**
 * Exercise-section session machine (MUS-315).
 *
 * The authoritative state machine for one exercise practice sitting:
 * family-agnostic (#113 - the same prompt -> response -> grade -> feedback
 * loop regardless of how the answer was captured) and the single state
 * authority (#115 - the engine owns session state; React reacts to the
 * informational events it emits and never drives state via effects).
 *
 * A sibling of the tutorial `exerciseMachine`, not an extension: it clones
 * the lifecycle vocabulary and the side-effect-action discipline
 * (`SIDE_EFFECT_ACTION_NAMES` stubs + `.provide({ actions })`, the
 * MUS-243 pattern) but is driven by user events, not video timing, and
 * carries none of the tutorial's objective/chapter/narration machinery.
 *
 * Two idioms, deliberately split:
 *   - Commands (scene/audio side effects) are provided actions - wired by
 *     the playable scene (MUS-317) / progress wiring (MUS-318), stubbed
 *     here.
 *   - Facts (the informational surface: playthroughCompleted, pointEarned,
 *     sessionCompleted) are xstate `emit()` events - typed, payload-first,
 *     runtime-subscribable via `actor.on()`. This is a deliberate,
 *     flagged divergence from MUS-243's snapshot-diff mechanics for the
 *     payload-carrying facts; phase observation itself still uses snapshot
 *     subscription.
 *
 * The clearance/mastery policy counter lives in context (within-sitting
 * state dies with the sitting, #128); its evaluation is the pure
 * `repeatabilityPolicy.ts`. Next-configuration choice is the pure,
 * swappable `planNextConfig` (`sessionTraversal.ts`).
 *
 * This PR wires the machine to nothing (7a). Consumers: the playable
 * scene (7b / MUS-317), Redux wiring (7c / MUS-318), grading (MUS-320).
 */

import { assign, emit, enqueueActions, setup } from 'xstate';
import { and, not, stateIn } from 'xstate/guards';

import type { ScaleCell, ScaleConfig, Verdict } from 'Types/ExerciseContentTypes';
import type {
  ExerciseSessionContext,
  ExerciseSessionEmitted,
  ExerciseSessionEvent,
  ExerciseSessionMachineInput,
  SessionExercise,
} from './exerciseSessionMachine.types';
import { policyMet, qualifiesForClearance, qualifiesForMastery } from './repeatabilityPolicy';
import { planNextConfig, takeKey } from './sessionTraversal';

/** Side-effect action names - commands, provided by the wiring layer
 *  (scene / saga) via `.provide({ actions })`; stubs that log until then.
 *  Tests override with spies. */
export const SIDE_EFFECT_ACTION_NAMES = [
  'loadContent', // fetch/prepare the session's content; answer with SESSION_LOADED
  'initPlaythrough', // prime the answer surface for a take
  'beginPlayback', // the response window opened (performed: transport/cursor)
  'stopPlayback', // the response window closed
  'gradeResponse', // grade context.pendingResponse; answer with GRADE_RESULT
  'showPassFeedback',
  'showFailFeedback',
  'pauseScene',
  'resumeScene',
  'cleanupSession',
] as const;

export type SideEffectActionName = (typeof SIDE_EFFECT_ACTION_NAMES)[number];

const defaultContext: ExerciseSessionContext = {
  exercise: null,
  currentCell: null,
  currentConfig: null,
  traversalPolicy: { kind: 'manual' },
  takes: {},
  earned: {},
  pendingResponse: null,
  lastResult: null,
  playthroughCount: 0,
  verdictCounts: { failed: 0, clean: 0, flawless: 0 },
  pointsEarned: [],
  paused: false,
};

function buildStubSideEffects(): Record<SideEffectActionName, () => void> {
  const map = {} as Record<SideEffectActionName, () => void>;
  for (const name of SIDE_EFFECT_ACTION_NAMES) {
    map[name] = () => {
      // eslint-disable-next-line no-console -- intentional stub logging
      console.log(`[SESSION-MACHINE-STUB] action: ${name}`);
    };
  }
  return map;
}

export const exerciseSessionMachine = setup({
  types: {
    context: {} as ExerciseSessionContext,
    events: {} as ExerciseSessionEvent,
    emitted: {} as ExerciseSessionEmitted,
    input: {} as ExerciseSessionMachineInput | undefined,
  },
  guards: {
    isNotPaused: ({ context }) => !context.paused,
    passedVerdict: ({ event }) =>
      event.type === 'GRADE_RESULT' && (event.verdict === 'clean' || event.verdict === 'flawless'),
    failedVerdict: ({ event }) => event.type === 'GRADE_RESULT' && event.verdict === 'failed',
    /** END_SESSION is meaningful only while a sitting is underway. */
    isSessionActive: and([not(stateIn('idle')), not(stateIn('sessionComplete'))]),
  },
  actions: {
    ...buildStubSideEffects(),

    seedSession: assign(({ event }) => {
      if (event.type !== 'LOAD_SESSION') return {};
      return {
        ...defaultContext,
        exercise: event.exercise,
        currentCell: event.cell,
        currentConfig: event.config,
        traversalPolicy: event.traversalPolicy ?? defaultContext.traversalPolicy,
      };
    }),

    storeResponse: assign({
      pendingResponse: ({ event }) => (event.type === 'SUBMIT_RESPONSE' ? event.response : null),
    }),

    setTraversalPolicy: assign({
      traversalPolicy: ({ context, event }) =>
        event.type === 'SET_TRAVERSAL_POLICY' ? event.policy : context.traversalPolicy,
    }),

    /**
     * The heart of playthrough-grain attribution (#128): record the take
     * against its (cell, config) counter, emit the playthrough fact, and -
     * when a clearance/mastery bar is newly met - emit the earned-point
     * fact that triggers the frontier write. Fire-once per bar per
     * take-key per sitting via the `earned` ledger.
     */
    processGradeResult: enqueueActions(({ context, event, enqueue }) => {
      if (event.type !== 'GRADE_RESULT') return;
      const { accuracy, verdict } = event;
      const cell = context.currentCell as ScaleCell;
      const config = context.currentConfig as ScaleConfig;
      const exercise = context.exercise as SessionExercise;
      const key = takeKey(cell, config);

      const nextTakes = [...(context.takes[key] ?? []), verdict];
      const prevEarned = context.earned[key] ?? { cleared: false, mastered: false };

      const clearedNow =
        !prevEarned.cleared &&
        policyMet(nextTakes, exercise.clearance_policy, qualifiesForClearance);
      const masteredNow =
        !prevEarned.mastered && policyMet(nextTakes, exercise.mastery_policy, qualifiesForMastery);
      // A mastered point dominates a cleared one (#128), so a take that
      // newly meets both bars earns a single mastered point.
      const earnsPoint = clearedNow || masteredNow;

      const playthroughIndex = context.playthroughCount + 1;
      const nextVerdictCounts: Record<Verdict, number> = {
        ...context.verdictCounts,
        [verdict]: context.verdictCounts[verdict] + 1,
      };

      enqueue.assign({
        takes: { ...context.takes, [key]: nextTakes },
        earned: {
          ...context.earned,
          [key]: {
            cleared: prevEarned.cleared || clearedNow || masteredNow,
            mastered: prevEarned.mastered || masteredNow,
          },
        },
        lastResult: { accuracy, verdict },
        pendingResponse: null,
        playthroughCount: playthroughIndex,
        verdictCounts: nextVerdictCounts,
        pointsEarned: earnsPoint
          ? [...context.pointsEarned, { cell, config, mastered: masteredNow }]
          : context.pointsEarned,
      });

      enqueue(
        emit({
          type: 'playthroughCompleted',
          cell,
          config,
          accuracy,
          verdict,
          playthroughIndex,
        }),
      );
      if (earnsPoint) {
        enqueue(emit({ type: 'pointEarned', cell, config, mastered: masteredNow }));
      }
    }),

    /** NEXT: choose the next playthrough's configuration (pure policy). */
    advanceConfig: assign(({ context }) => {
      const cell = context.currentCell as ScaleCell;
      const config = context.currentConfig as ScaleConfig;
      const clearedAtCurrent = (context.earned[takeKey(cell, config)] ?? { cleared: false })
        .cleared;
      return {
        currentConfig: planNextConfig(config, context.traversalPolicy, clearedAtCurrent),
        lastResult: null,
      };
    }),

    emitSessionCompleted: emit(({ context }) => ({
      type: 'sessionCompleted' as const,
      summary: {
        playthroughs: context.playthroughCount,
        verdictCounts: context.verdictCounts,
        pointsEarned: context.pointsEarned,
      },
    })),

    setPausedTrue: assign({ paused: () => true }),
    setPausedFalse: assign({ paused: () => false }),
    resetContext: assign(() => ({ ...defaultContext })),
  },
}).createMachine({
  id: 'exerciseSession',
  initial: 'idle',
  context: ({ input }) => ({
    ...defaultContext,
    ...(input ?? {}),
  }),

  on: {
    UNLOAD_SESSION: {
      target: '.idle',
      actions: ['cleanupSession', 'resetContext'],
    },
    END_SESSION: {
      target: '.sessionComplete',
      guard: 'isSessionActive',
    },
    SET_TRAVERSAL_POLICY: {
      actions: 'setTraversalPolicy',
    },
    PAUSE: {
      actions: ['setPausedTrue', 'pauseScene'],
    },
    RESUME: {
      actions: ['setPausedFalse', 'resumeScene'],
    },
  },

  states: {
    // -----------------------------------------------------------------
    idle: {
      on: {
        LOAD_SESSION: {
          target: 'loading',
          actions: 'seedSession',
        },
      },
    },

    // -----------------------------------------------------------------
    loading: {
      entry: ['loadContent'],
      on: {
        SESSION_LOADED: 'waitingForStart',
      },
    },

    // -----------------------------------------------------------------
    // The pre-practice "press start" wait - a real user-facing state
    // (unlike the tutorial machine's race-fix state of the same name).
    waitingForStart: {
      on: {
        START: 'waitingForInput',
      },
    },

    // -----------------------------------------------------------------
    waitingForInput: {
      entry: ['initPlaythrough'],
      on: {
        RESPONSE_STARTED: {
          target: 'playing',
          guard: 'isNotPaused',
        },
        // Some families submit without a response window (e.g. a
        // one-tap answered response) - the tutorial machine's
        // PHRASE_END-from-waitingForInput precedent.
        SUBMIT_RESPONSE: {
          target: 'evaluating',
          guard: 'isNotPaused',
          actions: 'storeResponse',
        },
      },
    },

    // -----------------------------------------------------------------
    playing: {
      entry: ['beginPlayback'],
      on: {
        SUBMIT_RESPONSE: {
          target: 'evaluating',
          actions: 'storeResponse',
        },
      },
    },

    // -----------------------------------------------------------------
    evaluating: {
      entry: ['stopPlayback', 'gradeResponse'],
      on: {
        GRADE_RESULT: [
          {
            target: 'passed',
            guard: 'passedVerdict',
            actions: 'processGradeResult',
          },
          {
            target: 'failed',
            guard: 'failedVerdict',
            actions: 'processGradeResult',
          },
        ],
        // MUS-319: a no-input take is a no-op, NOT a fail. It routes here with
        // NO `processGradeResult`, so takes/verdictCounts/earned are untouched and
        // an in-progress mastery streak is neither advanced nor broken. Without a
        // separate exit, suppressing the grade would strand the machine in
        // `evaluating` (its only other exits both score the take).
        GRADE_NO_INPUT: {
          target: 'noInput',
          // Clear the stored response so the no-op leaves no residue. This is the
          // ONLY state change a no-input take makes: takes/earned/verdictCounts/
          // playthroughCount are all untouched.
          actions: assign({ pendingResponse: null }),
        },
      },
    },

    // -----------------------------------------------------------------
    passed: {
      entry: ['showPassFeedback'],
      on: {
        NEXT: {
          target: 'waitingForInput',
          actions: 'advanceConfig',
        },
      },
    },

    // -----------------------------------------------------------------
    // NEXT from failed is the retry path: planNextConfig holds the
    // configuration (a failed take never meets a bar).
    failed: {
      entry: ['showFailFeedback'],
      on: {
        NEXT: {
          target: 'waitingForInput',
          actions: 'advanceConfig',
        },
      },
    },

    // -----------------------------------------------------------------
    // MUS-319: a submitted take that captured no played input. Shows the
    // "no input recorded" signal (the feedback renderer distinguishes it from
    // a real fail) but scores NOTHING - no processGradeResult ran, so the take
    // history, verdict counts, and any mastery streak are untouched. NEXT holds
    // the configuration (nothing cleared) and reopens the response window, the
    // same retry shape as `failed`.
    noInput: {
      entry: ['showFailFeedback'],
      on: {
        NEXT: {
          target: 'waitingForInput',
          actions: 'advanceConfig',
        },
      },
    },

    // -----------------------------------------------------------------
    // Not a final state, deliberately (a divergence from the tutorial
    // machine): the actor is reusable across sittings - UNLOAD_SESSION
    // returns it to idle without scene teardown.
    sessionComplete: {
      entry: ['emitSessionCompleted', 'cleanupSession'],
    },
  },
});

export type ExerciseSessionMachine = typeof exerciseSessionMachine;
